/**
 * Authentication state and actions for the SPA (task 21.1; Slice C3 rewrite).
 *
 * Slice C3 switches the SPA to authenticate **directly against Amazon Cognito**
 * (Option A) using {@link ./cognito}. MFA is ON on the pool using a TOTP
 * software token (authenticator app), so sign-in is TWO steps: email + password
 * first, then the current 6-digit code from the user's authenticator app. The
 * exported {@link AuthState} shape is kept as compatible as possible so the
 * rest of the app keeps working:
 *
 * - **Two-step sign-in** — {@link AuthState.submitCredentials} runs the Cognito
 *   `USER_PASSWORD_AUTH` password step. If Cognito issues a TOTP MFA challenge
 *   the provider moves to `otpRequired` and the SPA collects the authenticator
 *   code; {@link AuthState.submitOtp} completes the challenge and goes to
 *   `authenticated`. If no MFA challenge is raised the provider goes straight to
 *   `authenticated`. The stored bearer credential is the Cognito **idToken**
 *   (the JWT the API Gateway authorizer accepts).
 * - **Session restore on load** — on mount the provider calls
 *   `getSession()` (Cognito SDK, tokens persisted in `localStorage`), replacing
 *   the old silent `/auth/refresh` call.
 * - **Sign-out** — {@link AuthState.signOut} calls `signOutCognito()` and clears
 *   the in-memory token.
 *
 * The idToken lives only in {@link ./tokenStore} for header attachment; the
 * Cognito SDK owns cross-reload persistence.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  cancelTotpOtp,
  getSession,
  signInWithPassword,
  signOutCognito,
  submitTotpOtp,
  type CognitoSession,
} from './cognito.js';
import { ApiError } from './errors.js';
import { clearAccessToken, setAccessToken } from './tokenStore.js';

/**
 * The stage of the auth lifecycle the SPA is currently in.
 *
 * - `initializing` — the one-time session restore has not yet resolved.
 * - `signedOut` — no session; the sign-in form is shown.
 * - `otpRequired` — the password step raised a TOTP MFA challenge; the SPA is
 *   collecting the current 6-digit code from the user's authenticator app.
 * - `authenticated` — a valid idToken is held; app views are shown.
 */
export type AuthStatus = 'initializing' | 'signedOut' | 'otpRequired' | 'authenticated';

/** The auth state and actions exposed through {@link useAuth}. */
export interface AuthState {
  /** Current lifecycle stage. */
  readonly status: AuthStatus;
  /** The authenticated customer id (Cognito `sub`), present only when `authenticated`. */
  readonly customerId: string | undefined;
  /**
   * A human-friendly label for the signed-in user (name or email from the
   * token), for display only. Present only when `authenticated`.
   */
  readonly displayName: string | undefined;
  /**
   * The last operation's user-facing error message (non-sensitive), or
   * `undefined` when the last action succeeded or none has run.
   */
  readonly error: string | undefined;
  /** `true` while a sign-in / restore / sign-out request is in flight. */
  readonly pending: boolean;
  /**
   * The help line shown on the OTP step prompting the user to open their
   * authenticator app, present only while `status` is `otpRequired`.
   */
  readonly otpMessage: string | undefined;

  /**
   * Sign in with email + password (Cognito `USER_PASSWORD_AUTH`, password
   * step). On success with no MFA the idToken is stored and `status` becomes
   * `authenticated`; if Cognito raises a TOTP MFA challenge `status` becomes
   * `otpRequired` and {@link submitOtp} must be called with the current
   * authenticator app code. The parameter names are kept generic (`identifier`,
   * `credential`) for source-compatibility; they carry the email and password
   * respectively.
   */
  submitCredentials(identifier: string, credential: string): Promise<void>;
  /**
   * Complete the TOTP MFA challenge with the current 6-digit code from the
   * user's authenticator app. On success `status` becomes `authenticated`. A
   * wrong code keeps the user on `otpRequired` to retry with the next rotating
   * code; an expired session returns to `signedOut`.
   */
  submitOtp(code: string): Promise<void>;
  /** Abandon the OTP step and return to the signed-out (password) view. */
  cancelOtp(): void;
  /** End the session and return to the signed-out state (R1.10). */
  signOut(): Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

/** Extract a user-facing, non-sensitive message from a thrown value. */
function messageOf(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  return 'Something went wrong. Please try again.';
}

/**
 * Provides {@link AuthState} to the tree. Mount once near the root (see
 * {@link ../App}). Restores a persisted Cognito session on mount so a returning
 * user is signed back in automatically without re-entering credentials.
 */
export function AuthProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('initializing');
  const [customerId, setCustomerId] = useState<string | undefined>(undefined);
  const [displayName, setDisplayName] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState<boolean>(false);
  const [otpMessage, setOtpMessage] = useState<string | undefined>(undefined);

  /** Apply an established session: store the idToken in memory, mark signed in. */
  const applySession = useCallback((session: CognitoSession): void => {
    setAccessToken(session.idToken);
    setCustomerId(session.customerId);
    setDisplayName(session.displayName);
    setError(undefined);
    setOtpMessage(undefined);
    setStatus('authenticated');
  }, []);

  /** Reset all state back to signed-out and clear the in-memory token. */
  const resetToSignedOut = useCallback((): void => {
    clearAccessToken();
    setCustomerId(undefined);
    setDisplayName(undefined);
    setOtpMessage(undefined);
    setStatus('signedOut');
  }, []);

  // One-time session restore on mount: the Cognito SDK persists tokens in
  // localStorage, so a returning user with a still-valid session is signed
  // back in without re-authenticating (replaces the old silent refresh).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const session = await getSession();
      if (cancelled) {
        return;
      }
      if (session !== null) {
        applySession(session);
      } else {
        clearAccessToken();
        setStatus('signedOut');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applySession]);

  const submitCredentials = useCallback(
    async (identifier: string, credential: string): Promise<void> => {
      setPending(true);
      setError(undefined);
      try {
        const result = await signInWithPassword(identifier, credential);
        if (result.kind === 'session') {
          applySession(result.session);
        } else {
          setOtpMessage(
            'Open your authenticator app and enter the current 6-digit code.',
          );
          setStatus('otpRequired');
        }
      } catch (caught) {
        setError(messageOf(caught));
      } finally {
        setPending(false);
      }
    },
    [applySession],
  );

  const submitOtp = useCallback(
    async (code: string): Promise<void> => {
      setPending(true);
      setError(undefined);
      try {
        const session = await submitTotpOtp(code);
        applySession(session);
      } catch (caught) {
        setError(messageOf(caught));
        if (caught instanceof ApiError && caught.code === 'AUTH_REQUIRED') {
          // Terminal: the challenge can no longer be completed — send the user
          // back to the password step to start over.
          resetToSignedOut();
        }
        // Otherwise (e.g. a wrong code) stay on `otpRequired` so the user
        // retries with the next rotating code.
      } finally {
        setPending(false);
      }
    },
    [applySession, resetToSignedOut],
  );

  const cancelOtp = useCallback((): void => {
    cancelTotpOtp();
    setError(undefined);
    resetToSignedOut();
  }, [resetToSignedOut]);

  const signOut = useCallback(async (): Promise<void> => {
    setPending(true);
    setError(undefined);
    try {
      signOutCognito();
    } finally {
      resetToSignedOut();
      setPending(false);
    }
  }, [resetToSignedOut]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      customerId,
      displayName,
      error,
      pending,
      otpMessage,
      submitCredentials,
      submitOtp,
      cancelOtp,
      signOut,
    }),
    [
      status,
      customerId,
      displayName,
      error,
      pending,
      otpMessage,
      submitCredentials,
      submitOtp,
      cancelOtp,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Access the auth state and actions. Must be called inside an
 * {@link AuthProvider}; throws otherwise so misuse is caught early.
 */
export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider.');
  }
  return context;
}
